import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { TuettuKieli } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { Asiakas, Tilikausi } from '../model/asiakas'
import { AsiakkaanTilikartta, Kirjanpitotili, PaatilikartanProfiili, Paatilikartta } from '../model/kirjanpito'

export interface HierarkiaKirjanpitotili {
  kirjanpitotili: Kirjanpitotili
  lapset: HierarkiaKirjanpitotili[]
}

interface AsiakkaanTilikarttaJaPuretutTilit {
  tilikartta: AsiakkaanTilikartta
  tilit: Kirjanpitotili[]
}

export class TilikarttaJaettuService {
  constructor(
    private _kopioijaPalvelu: KopioijaPalvelu,
    private _dateService: DateService,
  ) {}

  annaKirjanpitotilinNimi(tili: Kirjanpitotili, kieli: TuettuKieli): string {
    if (tili?.localisedName && tili.localisedName[kieli]) {
      return tili.localisedName[kieli]
    }
    return tili?.nimi ?? ''
  }

  annaHierarkiaTili(tili: Kirjanpitotili, tilit: Kirjanpitotili[]): HierarkiaKirjanpitotili {
    const hierarkia: HierarkiaKirjanpitotili = {
      kirjanpitotili: tili,
      lapset: [],
    }
    const lapset = this.annaTilinLapset(tili, tilit)
    for (const lapsi of lapset) {
      hierarkia.lapset.push(this.annaHierarkiaTili(lapsi, tilit))
    }
    hierarkia.lapset.sort((a, b) => {
      return Number(a.kirjanpitotili.numero) - Number(b.kirjanpitotili.numero)
    })
    return hierarkia
  }

  private annaTilinLapset(tili: Kirjanpitotili, tilit: Kirjanpitotili[]): Kirjanpitotili[] {
    return tilit.filter(a => a.vanhempi === tili.numero)
  }

  muutaTililistausHierarkiaksi(tilit: Kirjanpitotili[]): HierarkiaKirjanpitotili[] {
    const p: HierarkiaKirjanpitotili[] = []
    for (const tili of tilit) {
      if (!tili.vanhempi) {
        p.push(this.annaHierarkiaTili(tili, tilit))
      }
    }
    return p.sort((a, b) => {
      return Number(a.kirjanpitotili.numero) - Number(b.kirjanpitotili.numero)
    })
  }

  private _lisaaLapsenKirjanpitoTilitHierarkianMukaisessaJarjestyksessa(lapsi: HierarkiaKirjanpitotili, tilit: Kirjanpitotili[]) {
    tilit.push(lapsi.kirjanpitotili)
    if (lapsi.lapset) {
      for (const lapsenlapsi of lapsi.lapset) {
        this._lisaaLapsenKirjanpitoTilitHierarkianMukaisessaJarjestyksessa(lapsenlapsi, tilit)
      }
    }
  }

  annaKirjanpitoTilitHierarkianMukaisessaJarjestyksessa(kaikki: HierarkiaKirjanpitotili[]): Kirjanpitotili[] {
    const tilit: Kirjanpitotili[] = []
    for (const hierarkia of kaikki) {
      tilit.push(hierarkia.kirjanpitotili)
      if (hierarkia.lapset) {
        for (const lapsi of hierarkia.lapset) {
          this._lisaaLapsenKirjanpitoTilitHierarkianMukaisessaJarjestyksessa(lapsi, tilit)
        }
      }
    }
    return tilit
  }

  private _kopioiTiliJaKayttaenPaatilikartanProfiilia(tili: Kirjanpitotili, profiili: PaatilikartanProfiili | null): Kirjanpitotili {
    const copy = this._kopioiTili(tili)

    // Jos ei profiilia, palauta copy
    if (!profiili?.t) {
      return copy
    }

    const p = profiili.t[copy.numero]

    // Jos profiilissa ei ole tälle numerolle tietoja, palauta copy
    if (!p) {
      return copy
    }

    // Hoida nimi
    if (p.nimi) {
      if (!copy.localisedName) {
        copy.localisedName = { fi: null, en: null }
      }
      for (const key of Object.keys(p.nimi)) {
        if (p.nimi[key]) {
          copy.localisedName[key] = p.nimi[key]
        }
      }
    }

    // Hoida aktiivisuus
    if (p.piilota) {
      copy.aktiivinen = false
      copy.piilotettuProfiilissa = true
    }

    return copy
  }

  private _kopioiTili(tili: Kirjanpitotili): Kirjanpitotili {
    const t: Kirjanpitotili = {
      vanhempi: tili.vanhempi,
      numero: tili.numero,
      nimi: tili.nimi,
      oletusAlvKasittely: tili.oletusAlvKasittely,
      aktiivinen: tili.aktiivinen,
      alvTyyppi: tili.alvTyyppi,
    }
    if (tili.reskontraActive !== undefined) {
      t.reskontraActive = !!tili.reskontraActive
    }
    if (tili.localisedName) {
      t.localisedName = this._kopioijaPalvelu.cloneObjectDeep(tili.localisedName)
    }
    return t
  }

  annaPaatilikarttaKayttaenProfiilia(paatilikartta: Paatilikartta, tilikausi: Tilikausi): Kirjanpitotili[] {
    const profiili = this.annaAsiakkaanTilikartanProfiili(paatilikartta, tilikausi)
    return this._annaPaatilikarttaKayttaenProfiilia(paatilikartta, profiili)
  }

  annaPaatilikarttaKayttaenProfiilinAvainta(paatilikartta: Paatilikartta, profiilinAvain: PaatilikartanProfiili['avain']): Kirjanpitotili[] {
    const profiili = this.annaAsiakkaanTilikartanProfiilinAvaimella(paatilikartta, profiilinAvain)
    return this._annaPaatilikarttaKayttaenProfiilia(paatilikartta, profiili)
  }

  private _annaPaatilikarttaKayttaenProfiilia(paatilikartta: Paatilikartta, profiili: PaatilikartanProfiili): Kirjanpitotili[] {
    const paatilikartanTilitAlkuperaiset: Kirjanpitotili[] = paatilikartta && paatilikartta.tilit ? Object.keys(paatilikartta.tilit).map(key => paatilikartta.tilit[key]) : []
    const paatilikartanTilit: Kirjanpitotili[] = paatilikartanTilitAlkuperaiset.map(tili => this._kopioiTiliJaKayttaenPaatilikartanProfiilia(tili, profiili))
    return paatilikartanTilit
  }

  private _annaEdellinenTilikausi(tilikaudet: Tilikausi[], tilikausi: Tilikausi): Tilikausi {
    if (!tilikausi) {
      return null
    }
    const endDayOfPrevious = this._dateService.lisaaPaiviaPaikallinen(tilikausi.alkaa, -1)
    return tilikaudet?.find(til => this._dateService.compareLocalDates(til.loppuu, '==', endDayOfPrevious))
  }

  yhdistaAsiakkaanTilikarttaPaatilikarttaanHuomioidenVaihdos(
    paatilikartta: Paatilikartta,
    asiakkaanTilikartta: AsiakkaanTilikartta,
    tilikausi: Tilikausi,
    asiakas: Pick<Asiakas, 'tilikaudet'>,
  ): Kirjanpitotili[] {
    const edellinenTilikausi = this._annaEdellinenTilikausi(asiakas.tilikaudet, tilikausi)

    // TODO: FIXME: DEFAULT FOR NOW A23
    const vaihdos = edellinenTilikausi && (edellinenTilikausi.tilikartanProfiili || 'maksuperusteinen') !== (tilikausi.tilikartanProfiili || 'maksuperusteinen')
    if (vaihdos) {
      const edellinenTilikartta = this.yhdistaAsiakkaantilikarttaPaatilikarttaan(paatilikartta, asiakkaanTilikartta, edellinenTilikausi)
      const nykyinenTilikartta = this.yhdistaAsiakkaantilikarttaPaatilikarttaan(paatilikartta, asiakkaanTilikartta, tilikausi)

      for (const edellinenTili of edellinenTilikartta) {
        let tili = nykyinenTilikartta.find(t => t.numero === edellinenTili.numero)
        if (!tili) {
          // Varmistetaan, ettei ole orpoa tiliä
          if (nykyinenTilikartta.find(t => t.numero === edellinenTili.vanhempi)) {
            tili = edellinenTili
            nykyinenTilikartta.push(edellinenTili)
          } else {
            throw new Error('Tilikauden vaihdoksen yhteydessä tilikartan profiilia vaihdettiin, mutta tilillä ' + edellinenTili.numero + ' ei ole nykyisessä tilikartassa vanhempaa tiliä.')
          }
        }
        if (tili.piilotettuProfiilissa && !edellinenTili.piilotettuProfiilissa) {
          tili.localisedName = edellinenTili.localisedName
          tili.nimi = edellinenTili.nimi
          tili.alvTyyppi = edellinenTili.alvTyyppi
          tili.oletusAlvKasittely = edellinenTili.oletusAlvKasittely
          tili.reskontraActive = edellinenTili.reskontraActive
          tili.poistotiedot = edellinenTili.poistotiedot
          tili.vanhempi = edellinenTili.vanhempi
          tili.aktiivinen = edellinenTili.aktiivinen
          delete tili.piilotettuProfiilissa
        }
      }
      return nykyinenTilikartta
    } else {
      // Jos ei vaihdosta, huomioi profiili normaalisti
      return this.yhdistaAsiakkaantilikarttaPaatilikarttaan(paatilikartta, asiakkaanTilikartta, tilikausi)
    }
  }

  yhdistaAsiakkaanTilikarttaPaatilikarttaanUseallaTilikaudella(
    paatilikartta: Paatilikartta,
    asiakkaanTilikartta: AsiakkaanTilikartta,
    tilikaudet: Tilikausi[],
    asiakas: Pick<Asiakas, 'tilikaudet'>,
  ): Kirjanpitotili[] {
    const maksuperusteinen = tilikaudet.some(t => (t.tilikartanProfiili || 'maksuperusteinen') === 'maksuperusteinen')
    const liikekirjuri = tilikaudet.some(t => t.tilikartanProfiili === 'liikekirjuri')

    if (liikekirjuri && maksuperusteinen) {
      const uusinTilikausi = tilikaudet.reduce((a, b) => (this._dateService.compareLocalDates(b.loppuu, '>', a.loppuu) ? b : a))
      const nykyinenProfiili = uusinTilikausi.tilikartanProfiili || 'maksuperusteinen'
      const vanhaProfiili: PaatilikartanProfiili['avain'] = nykyinenProfiili === 'liikekirjuri' ? 'maksuperusteinen' : 'liikekirjuri'

      const tilit = this.yhdistaAsiakkaantilikarttaPaatilikarttaanProfiilinAvaimella(paatilikartta, asiakkaanTilikartta, nykyinenProfiili)
      const vanhatTilit = this.yhdistaAsiakkaantilikarttaPaatilikarttaanProfiilinAvaimella(paatilikartta, asiakkaanTilikartta, vanhaProfiili)
      const vanhatTilitMap = new Map<string, Kirjanpitotili>(vanhatTilit.map(t => [t.numero, t]))

      // Vuosiluku haetaan kaikista asiakkaan tilikausista, ei vain raportilla näkyvistä
      const vanhanProfiilinViimeinenTilikausi = (asiakas?.tilikaudet ?? [])
        .filter(t => (t.tilikartanProfiili || 'maksuperusteinen') === vanhaProfiili)
        .reduce<Tilikausi>((a, b) => (!a || this._dateService.compareLocalDates(b.loppuu, '>', a.loppuu) ? b : a), null)
      const vuosi = vanhanProfiilinViimeinenTilikausi?.loppuu.year ?? null

      for (const tili of tilit) {
        const vanhaTili = vanhatTilitMap.get(tili.numero)
        // Asiakkaan omat tilit ovat sama raakaobjekti molemmissa listoissa, eikä niitä saa mutatoida
        if (!vanhaTili || tili === vanhaTili) {
          continue
        }
        if (tili.piilotettuProfiilissa && !vanhaTili.piilotettuProfiilissa) {
          // Tiliä on käytetty vain vanhan tilikartan aikana, joten sille näytetään vanhan tilikartan nimi
          tili.nimi = vanhaTili.nimi
          tili.localisedName = vanhaTili.localisedName
          continue
        }
        if (tili.piilotettuProfiilissa || vanhaTili.piilotettuProfiilissa || vuosi === null) {
          continue
        }
        for (const kieli of ['fi', 'en'] as TuettuKieli[]) {
          const nykyinenNimi = this.annaKirjanpitotilinNimi(tili, kieli)
          const vanhaNimi = this.annaKirjanpitotilinNimi(vanhaTili, kieli)
          if (nykyinenNimi && vanhaNimi && nykyinenNimi !== vanhaNimi) {
            if (!tili.localisedName) {
              tili.localisedName = { fi: null, en: null }
            }
            tili.localisedName[kieli] = `${nykyinenNimi} (${vuosi} ${vanhaNimi})`
          }
        }
      }

      for (const vanhaTili of vanhatTilit) {
        if (!tilit.find(t => t.numero === vanhaTili.numero)) {
          tilit.push(vanhaTili)
        }
      }
      return tilit
    } else if (maksuperusteinen && !liikekirjuri) {
      return this.yhdistaAsiakkaantilikarttaPaatilikarttaanProfiilinAvaimella(paatilikartta, asiakkaanTilikartta, 'maksuperusteinen')
    } else if (!maksuperusteinen && liikekirjuri) {
      return this.yhdistaAsiakkaantilikarttaPaatilikarttaanProfiilinAvaimella(paatilikartta, asiakkaanTilikartta, 'liikekirjuri')
    }

    // TODO: FIXME: DEFAULT FOR NOW A23
    return this.yhdistaAsiakkaantilikarttaPaatilikarttaanProfiilinAvaimella(paatilikartta, asiakkaanTilikartta, 'maksuperusteinen')
  }

  /**
   * Yhdistää päätilikartan asiakkaan tilimodifikaatioihin. Ei muokkaa päätilikarttaa
   * @param paatilikartta
   * @param asiakkaanTilikartta
   * @returns
   */
  yhdistaAsiakkaantilikarttaPaatilikarttaanProfiilinAvaimella(paatilikartta: Paatilikartta, asiakkaanTilikartta: AsiakkaanTilikartta, profiiliAvain: PaatilikartanProfiili['avain']): Kirjanpitotili[] {
    const profiili = this.annaAsiakkaanTilikartanProfiilinAvaimella(paatilikartta, profiiliAvain)
    return this._yhdistaAsiakkaantilikarttaPaatilikarttaanInternal(paatilikartta, asiakkaanTilikartta, profiili)
  }

  /**
   * Yhdistää päätilikartan asiakkaan tilimodifikaatioihin. Ei muokkaa päätilikarttaa
   * @param paatilikartta
   * @param asiakkaanTilikartta
   * @returns
   */
  yhdistaAsiakkaantilikarttaPaatilikarttaan(paatilikartta: Paatilikartta, asiakkaanTilikartta: AsiakkaanTilikartta, tilikausi: Tilikausi): Kirjanpitotili[] {
    const profiili = this.annaAsiakkaanTilikartanProfiili(paatilikartta, tilikausi)
    return this._yhdistaAsiakkaantilikarttaPaatilikarttaanInternal(paatilikartta, asiakkaanTilikartta, profiili)
  }

  private _yhdistaAsiakkaantilikarttaPaatilikarttaanInternal(paatilikartta: Paatilikartta, asiakkaanTilikartta: AsiakkaanTilikartta, profiili: PaatilikartanProfiili): Kirjanpitotili[] {
    const paatilikartanTilit = this._annaPaatilikarttaKayttaenProfiilia(paatilikartta, profiili)

    const asiakkanTilikarttaJaPuretutTilit: AsiakkaanTilikarttaJaPuretutTilit = {
      tilikartta: asiakkaanTilikartta,
      tilit: asiakkaanTilikartta?.tilit ? Object.keys(asiakkaanTilikartta.tilit).map(key => asiakkaanTilikartta.tilit[key]) : [],
    }

    const kaikkiTilit = paatilikartanTilit.concat(asiakkanTilikarttaJaPuretutTilit.tilit)
    const yliajotilit = asiakkaanTilikartta?.yliajotilit

    if (!yliajotilit) {
      // Nothing to customize, return
      return kaikkiTilit
    }

    // Process customizations
    for (const tili of kaikkiTilit) {
      if (yliajotilit && yliajotilit[tili.numero]) {
        const yliajotili = yliajotilit[tili.numero]
        tili.nimi = yliajotili.nimi

        tili.localisedName = yliajotili.localisedName ?? { fi: null, en: null }

        if (yliajotili.alvTyyppi !== undefined) {
          tili.alvTyyppi = yliajotili.alvTyyppi
        }
        if (yliajotili.oletusAlvKasittely !== undefined) {
          tili.oletusAlvKasittely = yliajotili.oletusAlvKasittely
        }
        if (yliajotili.reskontraActive !== undefined) {
          tili.reskontraActive = yliajotili.reskontraActive
        }
        if (yliajotili.poistotiedot !== undefined) {
          tili.poistotiedot = yliajotili.poistotiedot
        }
      }
    }

    return kaikkiTilit
  }

  annaAsiakkaanTilikartanProfiili(paatilikartta: Paatilikartta, tilikausi: Tilikausi): PaatilikartanProfiili | null {
    return this.annaAsiakkaanTilikartanProfiilinAvaimella(paatilikartta, tilikausi?.tilikartanProfiili)
  }

  annaAsiakkaanTilikartanProfiilinAvaimella(paatilikartta: Paatilikartta, profiilinAvain: PaatilikartanProfiili['avain']): PaatilikartanProfiili | null {
    if (!paatilikartta?.profiilit) {
      return null
    }
    if (profiilinAvain) {
      const profiili = paatilikartta.profiilit[profiilinAvain]
      if (!profiili) {
        throw new Error('Profiilia ' + profiilinAvain + ' ei löydy päätilikartasta.')
      }
      return profiili
    }
    return paatilikartta.profiilit['maksuperusteinen'] ?? null
  }

  etsiTiliHierarkiasta(numero: string, kaikki: HierarkiaKirjanpitotili[]): HierarkiaKirjanpitotili | null {
    for (const hierarkia of kaikki) {
      if (hierarkia.kirjanpitotili.numero === numero) {
        return hierarkia
      }
      if (hierarkia.lapset) {
        const loytynyt = this.etsiTiliHierarkiasta(numero, hierarkia.lapset)
        if (loytynyt) {
          return loytynyt
        }
      }
    }
    return null
  }

  /** Palauttaa tilin 1777 ja kaikki lapset */
  annaSelvitettavatOstotTilit(kaikki: HierarkiaKirjanpitotili[]): Kirjanpitotili[] {
    const flatatty: Kirjanpitotili[] = []
    if (kaikki) {
      const selvitettavatOstot = this.etsiTiliHierarkiasta('1777', kaikki)
      if (selvitettavatOstot) {
        this.flattaa(selvitettavatOstot, flatatty)
      }
    }
    return flatatty
  }

  /** Palauttaa tilin 1755 ja kaikki lapset */
  annaSaamisetOsakkailtaJaOmaisiltaTilit(kaikki: HierarkiaKirjanpitotili[]): Kirjanpitotili[] {
    const flatatty: Kirjanpitotili[] = []
    if (kaikki) {
      const saamisetOsakkailtaJaOmaisilta = this.etsiTiliHierarkiasta('1755', kaikki)
      if (saamisetOsakkailtaJaOmaisilta) {
        this.flattaa(saamisetOsakkailtaJaOmaisilta, flatatty)
      }
    }
    return flatatty
  }

  /** Palauttaa tilin 3099 ja kaikki lapset */
  annaSelvitettavatMyynnitTilit(kaikki: HierarkiaKirjanpitotili[]): Kirjanpitotili[] {
    const flatatty: Kirjanpitotili[] = []
    if (kaikki) {
      const selvitettavatMyynnit = this.etsiTiliHierarkiasta('3099', kaikki)
      if (selvitettavatMyynnit) {
        this.flattaa(selvitettavatMyynnit, flatatty)
      }
    }
    return flatatty
  }

  /** Palauttaa tilin 190 kaikki lapset */
  annaRahaJaPankkisaatavatTilit(kaikki: HierarkiaKirjanpitotili[]): Kirjanpitotili[] {
    const flatatty: Kirjanpitotili[] = []
    if (kaikki) {
      const rahaJaPankkisaamistili = this.etsiTiliHierarkiasta('190', kaikki)
      if (rahaJaPankkisaamistili?.lapset) {
        for (const hierarkiatili of rahaJaPankkisaamistili?.lapset) {
          this.flattaa(hierarkiatili, flatatty)
        }
      }
    }
    return flatatty
  }

  /** 40-99 */
  annaKulutilitMap(kaikki: HierarkiaKirjanpitotili[]): Map<string, Kirjanpitotili> {
    if (!kaikki) {
      return new Map()
    }
    const flatatty: Kirjanpitotili[] = []

    const start = 40
    const end = 99

    for (let i = start; i <= end; i++) {
      const tili = this.etsiTiliHierarkiasta('' + i, kaikki)
      if (!tili) {
        continue
      }
      this.flattaa(tili, flatatty)

      for (const hierarkiatili of tili?.lapset || []) {
        this.flattaa(hierarkiatili, flatatty)
      }
    }

    const output: Map<string, Kirjanpitotili> = new Map()

    for (const tili of flatatty) {
      output.set(tili.numero, tili)
    }

    return output
  }

  /** Palauttaa tuloslaskelman tilin 3 ja kaikkien lasten numerot */
  annaTuloslaskelmanTilinumerot(kaikki: HierarkiaKirjanpitotili[]): Set<string> {
    const flatatty: Kirjanpitotili[] = []
    const tuloslaskelma = kaikki ? this.etsiTiliHierarkiasta('3', kaikki) : null
    if (tuloslaskelma) {
      this.flattaa(tuloslaskelma, flatatty)
    }
    return new Set(flatatty.map(tili => tili.numero))
  }

  flattaa(hierarkiaTili: HierarkiaKirjanpitotili, flatatyt: Kirjanpitotili[]) {
    flatatyt.push(hierarkiaTili.kirjanpitotili)
    if (hierarkiaTili.lapset) {
      for (const h of hierarkiaTili.lapset) {
        this.flattaa(h, flatatyt)
      }
    }
  }
}
